import React, { useState } from 'react';
import { HardTagSelect } from '../../components/Tags/HardTagSelect';
import { SoftTagInput } from '../../components/Tags/SoftTagInput';
import { Tag } from '../../types/tag';

export const TagPlayground: React.FC = () => {
    // State for Hard Tags (ID strings)
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedTools, setSelectedTools] = useState<string[]>([]);

    // State for Soft Tags (Full objects)
    const [selectedStyles, setSelectedStyles] = useState<Tag[]>([]);
    const [selectedTopics, setSelectedTopics] = useState<Tag[]>([]);

    return (
        <div className="max-w-4xl mx-auto p-8 space-y-12">
            <div className="space-y-4">
                <h1 className="text-3xl font-bold text-slate-900">Tag System Playground</h1>
                <p className="text-slate-500">
                    A pure frontend implementation of the tag management system using mock data.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

                {/* Left Column: Input Components */}
                <div className="space-y-8">
                    <section className="space-y-4">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            Hard Tags <span className="text-sm font-normal text-slate-400">(Selection Only)</span>
                        </h2>

                        <div className="p-6 bg-white rounded-xl shadow-sm border border-slate-100 space-y-6">
                            <HardTagSelect
                                type="Category"
                                label="Service Category (Single Select)"
                                selectedTags={selectedCategories}
                                onChange={(ids) => setSelectedCategories(ids)}
                                multi={false} // Demo single select
                            />

                            <div className="h-px bg-slate-100 my-4"></div>

                            <HardTagSelect
                                type="Tool"
                                label="Tools / Models (Multi Select)"
                                selectedTags={selectedTools}
                                onChange={(ids) => setSelectedTools(ids)}
                                multi={true}
                            />
                        </div>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            Soft Tags <span className="text-sm font-normal text-slate-400">(Search + Create)</span>
                        </h2>

                        <div className="p-6 bg-white rounded-xl shadow-sm border border-slate-100 space-y-6">
                            <SoftTagInput
                                type="Style"
                                label="Visual Styles"
                                placeholder="e.g. Cyberpunk, Anime..."
                                selectedTags={selectedStyles}
                                onChange={setSelectedStyles}
                            />

                            <div className="h-px bg-slate-100 my-4"></div>

                            <SoftTagInput
                                type="Topic"
                                label="Topics (Max 3)"
                                placeholder="Enter topics..."
                                selectedTags={selectedTopics}
                                onChange={setSelectedTopics}
                                maxTags={3}
                            />
                        </div>
                    </section>
                </div>

                {/* Right Column: JSON Output */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Live Data Selection</h2>
                    <div className="sticky top-8">
                        <div className="bg-slate-900 rounded-xl overflow-hidden shadow-lg">
                            <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center">
                                <div className="flex gap-1.5 mr-3">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                <span className="text-slate-400 text-xs font-mono">selection_state.json</span>
                            </div>
                            <div className="p-4 overflow-x-auto">
                                <pre className="text-emerald-400 font-mono text-xs leading-relaxed">
                                    {JSON.stringify({
                                        category: selectedCategories,
                                        tools: selectedTools,
                                        styles: selectedStyles.map(t => ({ id: t.id, name: t.name })),
                                        topics: selectedTopics.map(t => ({ id: t.id, name: t.name })),
                                    }, null, 2)}
                                </pre>
                            </div>
                        </div>

                        <div className="mt-6 p-4 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100">
                            <p className="font-semibold mb-1">💡 Implementation Notes:</p>
                            <ul className="list-disc pl-4 space-y-1 opacity-90">
                                <li><strong>Hard Tags</strong> load all options upfront and only store IDs.</li>
                                <li><strong>Soft Tags</strong> use async search and store full objects (since they might be custom created).</li>
                                <li><strong>Mock Delay</strong> is set to 300ms to simulate network latency.</li>
                            </ul>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};
